import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from './supabaseClient';
import { FileUpload } from './components/FileUpload';
import { JobDescriptionForm } from './components/JobDescriptionForm';
import { CandidatesTable } from './components/CandidatesTable';
import { BrainCircuit, Search, X, Home, FileText, LifeBuoy, Package, LogOut, User, BarChart, Settings, Loader, AlertCircle, Upload, RefreshCw, Download } from 'lucide-react';
import { Candidate, JobDescription } from './types';
import { extractTextFromPDF } from './services/pdfService';
import { analyzeCandidates } from './services/aiService';
import { AnalysisLoading } from './components/AnalysisLoading';
import { AnalysisResults } from './components/AnalysisResults';
import { LanguageProvider } from './contexts/LanguageContext';
import { LanguageSelector } from './components/LanguageSelector';
import { useLanguage } from './contexts/LanguageContext';
import { checkUserCanUpload, incrementUserUsage } from './services/userService';
import { UsageLimitModal } from './components/UsageLimitModal';
import { HomePage } from './components/HomePage';
import { PricingPage } from './components/PricingPage';
import { 
  SubscriptionPlan, 
  PLAN_LIMITS, 
  checkUserCanAnalyze,
  incrementAnalysesUsed,
  cleanupUserSubscriptions,
  forceRefreshSubscription,
  subscribeToUserSubscriptionChanges
} from './services/subscriptionService';
import { SubscriptionSuccess } from './components/SubscriptionSuccess';
import { SubscriptionUsageTracker } from './components/SubscriptionUsageTracker';
import { SubscriptionAdminDashboard } from './components/SubscriptionAdminDashboard';
import UsageDashboard from './components/UsageDashboard';
import UsageIndicator from './components/UsageIndicator';
import { exportResultsToCsv } from './services/exportService';

function formatCandidateName(fileName: string): string {
  const nameWithoutExt = fileName.replace(/\.pdf$/i, '');
  const nameWithSpaces = nameWithoutExt.replace(/[_-]/g, ' ');
  return nameWithSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface UnanalyzedCandidate {
  id: string;
  name: string;
  resumeText: string;
  fileName: string;
}

const AuthContext = createContext<{ session: Session | null; loading: boolean }>({ session: null, loading: true });

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setSession(session);
      } catch (error) {
        console.error('Error fetching initial session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', _event, session ? 'Got session' : 'No session');
      setSession(session);
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <Loader className="h-8 w-8 animate-spin text-indigo-600" />
            <p className="ml-3 text-gray-700">Loading session...</p> 
        </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function AppContent() {
  const { t } = useLanguage();
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const supabaseUser = session?.user;
  const [candidates, setCandidates] = React.useState<UnanalyzedCandidate[]>([]);
  const [analyzedCandidates, setAnalyzedCandidates] = React.useState<Candidate[]>([]);
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [jobDescription, setJobDescription] = React.useState<JobDescription | null>(null);
  const [showUsageModal, setShowUsageModal] = React.useState(false);
  const [lastSubscriptionUpdate, setLastSubscriptionUpdate] = React.useState<number>(Date.now());
  const [userProfile, setUserProfile] = React.useState<{full_name?: string} | null>(null);
  const [updateCounter, setUpdateCounter] = React.useState(0);
  const [userUsage, setUserUsage] = React.useState<{
    canUpload: boolean;
    remainingUploads: number;
    isAdmin: boolean;
    totalUploads: number;
    currentPlan: SubscriptionPlan;
  }>({
    canUpload: true,
    remainingUploads: PLAN_LIMITS[SubscriptionPlan.FREE],
    isAdmin: false,
    totalUploads: PLAN_LIMITS[SubscriptionPlan.FREE],
    currentPlan: SubscriptionPlan.FREE
  });
  const [activeSection, setActiveSection] = useState('analyzer');
  const [showSidebarMobile, setShowSidebarMobile] = useState(false);
  const successfulAnalysesCount = useRef(0); // Ref to count successful analyses
  const [isExporting, setIsExporting] = useState(false);

  // Function to force a re-render
  const forceUpdate = () => {
    setUpdateCounter(prev => prev + 1);
  };

  // Function to update usage information across the application
  const refreshUsageInfo = () => {
    console.log('Triggering app-wide usage refresh');
    
    // Add a small delay to allow any other operations to complete
    setTimeout(() => {
      // Use a single event and add a timestamp to make it unique
      const event = new CustomEvent('usage-updated', { 
        detail: { timestamp: Date.now() } 
      });
      window.dispatchEvent(event);
      setLastSubscriptionUpdate(Date.now());
    }, 300);
  };

  // Fetch and update user profile information
  React.useEffect(() => {
    async function fetchUserProfile() {
      if (!supabaseUser) return;
      
        try {
        // Get user data
          const { data, error } = await supabase
            .from('profiles')
          .select('*')
            .eq('id', supabaseUser.id)
            .single();
          
          if (error) {
            console.error('Error fetching user profile:', error);
            return;
          }
          
          if (data) {
            setUserProfile(data);
          
          // Refresh usage information when user profile is loaded
          refreshUsageInfo();
          }
      } catch (error) {
        console.error('Error in fetchUserProfile effect:', error);
        }
    }
      
      fetchUserProfile();
  }, [supabaseUser]);

  // Check if the user just came from a subscription success page
  React.useEffect(() => {
    const prevPath = location.state?.from?.pathname || '';
    if (prevPath.includes('/subscription/success')) {
      console.log('User returned from subscription success page, refreshing subscription data');
      setLastSubscriptionUpdate(Date.now());
      
      // Force refresh subscription data immediately after success
      if (supabaseUser?.id) {
        forceRefreshSubscription(supabaseUser.id)
          .then((subscription) => {
            if (subscription) {
              console.log('Subscription forcefully refreshed:', subscription);
              // Update UI immediately with the new subscription data
              const subCheck = {
                canAnalyze: true,
                remainingAnalyses: subscription.analyses_limit - subscription.analyses_used,
                currentPlan: subscription.plan_type,
                isActive: subscription.is_active
              };
              
              setUserUsage(prev => ({
                ...prev,
                remainingUploads: subCheck.remainingAnalyses,
                currentPlan: subCheck.currentPlan,
                totalUploads: PLAN_LIMITS[subCheck.currentPlan] || PLAN_LIMITS[SubscriptionPlan.FREE]
              }));
            }
          })
          .catch(err => {
            console.error('Error force refreshing subscription:', err);
          });
      }
    }
  }, [location, supabaseUser?.id]);

  // Set up real-time subscription updates
  React.useEffect(() => {
    const currentUserId = supabaseUser?.id;
    if (!currentUserId) return;
    
    console.log('Setting up real-time subscription tracking for user:', currentUserId);
    
    // Subscribe to changes in subscription data
    const unsubscribe = subscribeToUserSubscriptionChanges(currentUserId, (updatedSubscription) => {
      console.log('Received real-time subscription update:', updatedSubscription);
      
      // Update UI with new subscription data
      const subCheck = {
        canAnalyze: true,
        remainingAnalyses: updatedSubscription.analyses_limit - updatedSubscription.analyses_used,
        currentPlan: updatedSubscription.plan_type,
        isActive: updatedSubscription.is_active
      };
      
      setUserUsage(prev => {
        const newUsage = {
          ...prev,
          remainingUploads: subCheck.remainingAnalyses,
          currentPlan: subCheck.currentPlan,
          totalUploads: PLAN_LIMITS[subCheck.currentPlan] || PLAN_LIMITS[SubscriptionPlan.FREE]
        };
        console.log('Updating user usage state via real-time:', newUsage);
        return newUsage;
      });
    });
    
    // Clean up subscription when component unmounts
    return () => {
      console.log('Cleaning up real-time subscription');
      unsubscribe();
    };
  }, [supabaseUser?.id]);

  React.useEffect(() => {
    async function checkUsage() {
      const currentUserId = supabaseUser?.id;
      if (currentUserId) {
        try {
            // First clean up any duplicate subscriptions
            console.log('Initializing and cleaning up subscriptions');
            await cleanupUserSubscriptions(currentUserId);
            
            // Try to sync any local usage data with Supabase
            try {
              const { syncLocalUsageData } = await import('./services/usageService');
              await syncLocalUsageData();
            } catch (syncError) {
              console.error('Failed to sync local usage data:', syncError);
            }
            
            const basicUsage = await checkUserCanUpload(currentUserId);
            const subscriptionData = await checkUserCanAnalyze(currentUserId);

            console.log('Retrieved subscription data:', subscriptionData);

            const combinedUsage = {
              canUpload: basicUsage.canUpload && subscriptionData.canAnalyze,
              remainingUploads: subscriptionData.remainingAnalyses,
              isAdmin: basicUsage.isAdmin,
              totalUploads: PLAN_LIMITS[subscriptionData.currentPlan] || PLAN_LIMITS[SubscriptionPlan.FREE],
              currentPlan: subscriptionData.currentPlan
            };
            setUserUsage(combinedUsage);
            console.log('Updated user usage state:', combinedUsage);

            if (!combinedUsage.canUpload || (combinedUsage.totalUploads > 0 && combinedUsage.remainingUploads / combinedUsage.totalUploads <= 0.2)) {
          setShowUsageModal(true);
            }

        } catch (err) {
            console.error("Error in checkUsage effect:", err);
            setError("Could not fetch usage details.");
        }
      } else {
         setUserUsage({
           canUpload: true,
           remainingUploads: PLAN_LIMITS[SubscriptionPlan.FREE],
           isAdmin: false,
           totalUploads: PLAN_LIMITS[SubscriptionPlan.FREE],
           currentPlan: SubscriptionPlan.FREE
         });
      }
    }
    checkUsage();
  }, [supabaseUser?.id, session]);

  const clearError = useCallback(() => {
    if (error) setError(null);
  }, [error]);

  const handleFileUpload = async (files: File[]) => {
    const currentUserId = supabaseUser?.id;
    if (!currentUserId) {
      setError(t('error.signInToUpload', 'Please sign in to upload resumes'));
      return;
    }
    if (files.length === 0) return;

    clearError();

    try {
      const checkResult = await checkUserCanAnalyze(currentUserId);
      setUserUsage(prev => ({
         ...prev,
         canUpload: checkResult.canAnalyze,
         remainingUploads: checkResult.remainingAnalyses,
         currentPlan: checkResult.currentPlan,
         totalUploads: PLAN_LIMITS[checkResult.currentPlan]
      }));

      if (!checkResult.canAnalyze) {
        setShowUsageModal(true);
        return;
      }
      if (!userUsage.isAdmin && checkResult.remainingAnalyses < files.length) {
        setError(t('error.uploadLimitExceeded', `You only have ${checkResult.remainingAnalyses} analysis slots left, but tried to upload ${files.length} files.`));
        setShowUsageModal(true);
        return;
      }
    } catch (err) {
       console.error("Error checking usage before upload:", err);
       setError(t('error.usageCheckFailed', 'There was an error checking your usage. Please try again later.'));
       return;
    }

      setIsProcessing(true);
      console.log('Processing files:', files.length);
      
    try {
      const newCandidates = await Promise.all(
        files.map(async (file, index) => {
          try {
            console.log(`Processing file: ${file.name}`);
            const resumeText = await extractTextFromPDF(file);
            console.log(`Extracted text from ${file.name}`);
            const candidateName = formatCandidateName(file.name);
            return {
              id: `candidate-${Date.now()}-${index}`,
              name: candidateName,
              resumeText,
              fileName: file.name,
            } as UnanalyzedCandidate;
          } catch (err) {
            console.error(`Failed to process file ${file.name}:`, err);
            throw new Error(t('error.fileProcessingFailed', 'There was an error processing the file. Please try again later.'));
          }
        })
      );

      console.log('Successfully processed files:', newCandidates.length);
      setCandidates(prev => [...prev, ...newCandidates]);
      setAnalyzedCandidates([]);

    } catch (err) {
      console.error('Error processing files:', err);
      setError(err instanceof Error ? err.message : t('error.fileProcessingGeneric', 'There was an error processing the files. Please try again later.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAnalyzeClick = async () => {
    const currentUserId = supabaseUser?.id;
    successfulAnalysesCount.current = 0; // Reset counter at the start
    
    // Import necessary functions from services AT THE TOP of the function
    const { canPerformAnalysis, trackUsage } = await import('./services/usageService');
    const { analyzeMultipleCandidatesWithProgressiveUpdates } = await import('./services/ai/aiService');
    const { checkUserCanAnalyze } = await import('./services/subscriptionService'); // Moved import here

    if (!jobDescription) {
      setError(t('error.submitJobDescription', 'Please submit a job description'));
      return;
    }
    if (candidates.length === 0) {
      setError(t('error.uploadResumes', 'Please upload resumes before analyzing'));
      return;
    }
    if (!currentUserId) {
      setError(t('error.signInToAnalyze', 'Please sign in to analyze'));
      return;
    }

    clearError();

    // --- New Check: Compare staged candidates with remaining slots ---
    if (candidates.length > userUsage.remainingUploads) {
      setError(t('error.analysisLimitExceededDetailed', 
        `You have ${candidates.length} resumes ready for analysis, but only ${userUsage.remainingUploads} analysis slots remaining in your plan. Please remove some resumes or upgrade your plan.`
      ));
      setShowUsageModal(true);
      return; // Stop before setting isAnalyzing or calling API
    }
    // --- End New Check ---

    setIsAnalyzing(true);
    // Create a new array to hold results as they arrive
    setAnalyzedCandidates([]);

    try {
      // First check if user can perform the analysis
      const permissionCheck = await canPerformAnalysis();
      
      if (!permissionCheck.canPerform) {
        setShowUsageModal(true);
        setError(t('error.analysisLimitReached', 'The analysis limit has been reached'));
        setIsAnalyzing(false);
        return;
      }

      console.log('Starting progressive analysis with job description:', jobDescription.title);
      console.log('Candidates to analyze:', candidates.length);
      
      // Callback function to handle each result as it arrives
      const handleResultCallback = (result: any, candidateId: string) => {
        // Find the candidate that matches this result
        const candidate = candidates.find(c => c.id === candidateId);
        if (!candidate) {
          console.error(`No matching candidate found for ID ${candidateId}`);
          return;
        }
        
        console.log(`Received analysis result for candidate: ${candidate.fileName}`, {
          candidateId,
          matchScore: result.matchScore,
          experienceScore: result.experienceScore,
          skillsScore: result.skillsScore
        });
        
        // Create an analyzed candidate by combining the candidate data with the analysis result
        const analyzedCandidate = {
          ...candidate,
          ...result,
        };
        
        console.log('Updating analyzed candidates state with new result');
        
        // Update the analyzed candidates array
        setAnalyzedCandidates(prev => {
          const newResults = [...prev];
          const existingIndex = newResults.findIndex(c => c.id === candidateId);
          
          if (existingIndex >= 0) {
            // Replace existing result
            console.log(`Replacing existing result at index ${existingIndex}`);
            newResults[existingIndex] = analyzedCandidate;
          } else {
            // Add new result
            console.log(`Adding new result, current count: ${newResults.length}`);
            newResults.push(analyzedCandidate);
          }
          
          // Sort by match score
          const sortedResults = newResults.sort((a, b) => b.matchScore - a.matchScore);
          console.log(`Sorted results, new count: ${sortedResults.length}`);
          return sortedResults;
        });
        
        // Increment the success counter
        successfulAnalysesCount.current += 1;

        // Remove this candidate from the candidates array
        console.log(`Removing candidate ${candidateId} from candidates list`);
        setCandidates(prev => {
          const filtered = prev.filter(c => c.id !== candidateId);
          console.log(`Candidates remaining: ${filtered.length}`);
          return filtered;
        });
        
        // Force a re-render to update the UI immediately
        console.log('Forcing UI update with new analysis result');
        forceUpdate();
      };
      
      // Start the progressive analysis
      await analyzeMultipleCandidatesWithProgressiveUpdates(
        candidates as any, // Type cast to bypass type checking - we know the callback will handle any missing properties
        jobDescription,
        handleResultCallback
      );
      
      console.log('Progressive analysis completed for all candidates');

    } catch (err) {
      console.error('Progressive analysis failed:', err);
      setError(err instanceof Error ? err.message : t('error.analysisFailedGeneric', 'There was an error analyzing the resumes. Please try again later.'));
    } finally {
      setIsAnalyzing(false);
      // Track usage based on the number of successfully processed candidates
      const analyzedCount = successfulAnalysesCount.current;
      if (analyzedCount > 0) {
        console.log(`Attempting to track usage for ${analyzedCount} successfully analyzed resumes.`);
        try {
          const success = await trackUsage('resume_analysis', analyzedCount);
      if (!success) {
            console.warn('Failed to track usage after analysis completion.');
          }
        } catch (trackError) {
            console.error('Error during final usage tracking:', trackError);
        }
      } else {
         console.log('No successful analyses to track usage for.');
      }
      
      // Update UI with latest subscription data AFTER usage tracking attempt
      if (currentUserId) { 
        try {
          console.log('Refreshing subscription data after analysis run.');
        const currentStatus = await checkUserCanAnalyze(currentUserId);
        setUserUsage(prev => ({
           ...prev,
           canUpload: currentStatus.canAnalyze,
           remainingUploads: currentStatus.remainingAnalyses,
           currentPlan: currentStatus.currentPlan,
            totalUploads: PLAN_LIMITS[currentStatus.currentPlan] || PLAN_LIMITS[SubscriptionPlan.FREE]
        }));
        
          // Check if we need to show usage limit warning after potential update
          const totalUploads = PLAN_LIMITS[currentStatus.currentPlan] || PLAN_LIMITS[SubscriptionPlan.FREE];
        const showWarning = !currentStatus.canAnalyze || 
            (totalUploads > 0 && currentStatus.remainingAnalyses / totalUploads <= 0.2);
        
        if (showWarning) {
          setShowUsageModal(true);
        }
          
          // Refresh usage information across the application
          refreshUsageInfo();
      } catch (error) {
          console.error('Failed to update subscription data after analysis run:', error);
        }
      }
    }
  };

  const handleJobSubmit = useCallback(async (job: JobDescription) => {
    try {
      clearError();
      console.log('Job description submitted:', job.title);
      setJobDescription(job);
    } catch (err) {
      console.error('Job submission failed:', err);
      setError(err instanceof Error ? err.message : t('error.jobSubmissionFailed', 'There was an error submitting the job. Please try again later.'));
    }
  }, [clearError, t]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error logging out:", error);
      setError("Failed to log out.");
    } else {
      navigate('/');
    }
  };

  const canAnalyze = candidates.length > 0 && jobDescription !== null && !isAnalyzing && !isProcessing;
  
  // Calculate usage percentage for progress bar
  let usagePercentage = 0;
  console.log('Calculating usage percentage for progress bar', userUsage);
  if (userUsage.totalUploads > 0) {
    const used = userUsage.totalUploads - userUsage.remainingUploads;
    usagePercentage = Math.min(100, Math.round((used / userUsage.totalUploads) * 100));
    console.log(`Usage: ${used} of ${userUsage.totalUploads} = ${usagePercentage}%`);
  } else {
    console.log('Total uploads is zero or not set');
  }

  // Determine color for usage meter
  const getUsageColor = () => {
    if (usagePercentage >= 90) return 'bg-gradient-to-r from-red-500 to-pink-500';
    if (usagePercentage >= 70) return 'bg-gradient-to-r from-yellow-400 to-orange-500';
    return 'bg-gradient-to-r from-green-400 to-emerald-500';
  };
  
  // Get plan emoji
  const getPlanEmoji = () => {
    switch(userUsage.currentPlan) {
      case SubscriptionPlan.STANDARD:
        return '✨';
      case SubscriptionPlan.PREMIUM:
        return '🌟';
      case SubscriptionPlan.FREE:
      default:
        return '🔹';
    }
  };

  const handleExportClick = async () => {
    if (!analyzedCandidates || analyzedCandidates.length === 0) return;
    if (userUsage.currentPlan !== SubscriptionPlan.STANDARD && userUsage.currentPlan !== SubscriptionPlan.PREMIUM) return;

    setIsExporting(true);
    try {
      // Call the export function
      await exportResultsToCsv(analyzedCandidates, jobDescription?.title || 'Analysis');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export results to CSV.'); // Show error in UI
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-30">
        <button
          onClick={() => setShowSidebarMobile(!showSidebarMobile)}
          className="p-2 rounded-full bg-white shadow-md text-indigo-600 hover:bg-indigo-50 transition-colors"
        >
          {showSidebarMobile ? <X className="h-6 w-6" /> : <BrainCircuit className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar - desktop & mobile */}
      <aside 
        className={`z-20 w-80 bg-white shadow-xl transform transition-all duration-300 ease-in-out flex flex-col
          ${showSidebarMobile ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 lg:shadow-md
          fixed inset-y-0 left-0 lg:static lg:inset-auto`}
      >
        {/* Logo section */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-center lg:justify-start space-x-3">
            <div className="p-2 bg-gradient-to-br from-indigo-600 to-blue-500 rounded-xl">
              <BrainCircuit className="h-7 w-7 text-white" />
            </div>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-500">
              IRecruitor
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-3 overflow-y-auto">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">
            Main
          </div>
          <button
            onClick={() => setActiveSection('analyzer')}
            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${
              activeSection === 'analyzer'
                ? 'bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <Search className={`h-5 w-5 mr-3 ${activeSection === 'analyzer' ? 'text-indigo-500' : 'text-gray-500'}`} />
            <span className="font-medium">{t('sidebar.analyzer', 'Resume Analyzer')}</span>
          </button>
          <button
            onClick={() => setActiveSection('dashboard')}
            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${
              activeSection === 'dashboard'
                ? 'bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <BarChart className={`h-5 w-5 mr-3 ${activeSection === 'dashboard' ? 'text-indigo-500' : 'text-gray-500'}`} />
            <span className="font-medium">{t('sidebar.dashboard', 'Dashboard')}</span>
          </button>
        </nav>

        {/* Subscription & usage section */}
        <div className="p-5 border-t border-gray-100 space-y-5">
          {/* Subscription card - replace with UsageIndicator component */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">
                  {getPlanEmoji()} {userUsage.currentPlan.charAt(0).toUpperCase() + userUsage.currentPlan.slice(1)} Plan
                </span>
              </div>
              <Link 
                to="/pricing" 
                className="px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full hover:from-indigo-700 hover:to-blue-700 transition-all shadow-sm hover:shadow"
              >
                Upgrade Now
              </Link>
            </div>
            
            <div className="space-y-2">
              <UsageIndicator 
                compact 
                usageData={{ 
                  usedCount: userUsage.totalUploads - userUsage.remainingUploads,
                  limit: userUsage.totalUploads,
                  remaining: userUsage.remainingUploads,
                  percentage: usagePercentage,
                  canPerformAnalysis: userUsage.canUpload,
                  hasActiveSubscription: true,
                  planType: userUsage.currentPlan
                }}
              />
            </div>
          </div>
          
          {/* Language & Profile section */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">{supabaseUser?.email?.split('@')[0]}</span>
            </div>
            <LanguageSelector />
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors font-medium"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('sidebar.logout', 'Logout')}
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {showSidebarMobile && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
          onClick={() => setShowSidebarMobile(false)}
        />
      )}

      {/* Main content area */}
      <main className="flex-1 p-4 lg:p-8 max-w-7xl mx-auto w-full">
        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-xl shadow-sm animate-fade-in flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <span>{error}</span>
            </div>
            <button onClick={clearError} className="text-red-400 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {/* Analyzer section */}
        {activeSection === 'analyzer' && (
          <div className="space-y-8">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">
                  {t('app.title', 'AI Resume Analyzer')}
                </h1>
                <p className="text-gray-600 max-w-2xl">
                  Upload resumes and job descriptions to find the perfect match using AI-powered analysis.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Job Description Form */}
              <div className="bg-white rounded-2xl shadow-md overflow-hidden transition-shadow hover:shadow-lg">
                <div className="bg-gradient-to-r from-indigo-500 to-blue-500 p-4 text-white">
                  <h2 className="font-bold text-lg flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Job Description
                  </h2>
                </div>
                <div className="p-1">
                  <JobDescriptionForm onSubmit={handleJobSubmit} />
                </div>
              </div>

              {/* File Upload */}
              <div className="bg-white rounded-2xl shadow-md overflow-hidden transition-shadow hover:shadow-lg">
                <div className="bg-gradient-to-r from-indigo-500 to-blue-500 p-4 text-white">
                  <h2 className="font-bold text-lg flex items-center">
                    <Upload className="h-5 w-5 mr-2" />
                    Resume Upload
                </h2>
                </div>
                <div className="p-6">
            <FileUpload 
              onFileUpload={handleFileUpload}
              isProcessing={isProcessing}
                  />
                </div>
              </div>
          </div>

            {/* Analyze button section */}
            <div className="text-center my-8">
              <button
                onClick={handleAnalyzeClick}
                disabled={!canAnalyze}
                className={`px-8 py-4 rounded-2xl font-bold text-white transition-all duration-300 shadow-md hover:shadow-lg ${
                  canAnalyze 
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 cursor-pointer'
                    : 'bg-gray-400 cursor-not-allowed opacity-75'
                }`}
              >
                <span className="flex items-center justify-center">
                  {isAnalyzing ? t('app.analyzing', 'Analyzing...') : t('app.analyzeButton', 'Analyze Resumes')}
                  {isAnalyzing ? (
                    <Loader className="ml-2 h-5 w-5 animate-spin" />
                  ) : (
                    <Search className="ml-2 h-5 w-5" />
                  )}
                </span>
              </button>
            </div>

            {/* List of candidates ready for analysis */}
            {candidates.length > 0 && !isAnalyzing && analyzedCandidates.length === 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 shadow-sm animate-fade-in">
                <h3 className="text-xl font-semibold text-indigo-800 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-indigo-600" />
                  {t('app.readyToAnalyze', 'Candidates Ready for Analysis')}
                  <span className="ml-2 bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {candidates.length}
                  </span>
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {candidates.map(c => (
                    <li key={c.id} className="bg-white p-3 rounded-lg border border-blue-100 flex items-center">
                      <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                        <FileText className="h-4 w-4 text-indigo-700" />
          </div>
                      <span className="text-sm text-gray-700 truncate" title={c.fileName}>
                        {c.fileName}
                      </span>
                    </li>
                  ))}
                </ul>
        </div>
            )}

            {/* Analysis Results or Loading state */}
          {isAnalyzing ? (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-blue-500 p-4 text-white">
                  <h2 className="font-bold text-lg flex items-center">
                    <BarChart className="h-5 w-5 mr-2" />
                    Analysis Results {analyzedCandidates.length > 0 ? `(${analyzedCandidates.length} completed)` : ''}
                  </h2>
                </div>
                <div className="p-1">
                  {analyzedCandidates.length > 0 ? (
                    <>
                      <AnalysisResults 
                        results={analyzedCandidates} 
                        isLoading={false} 
                        jobDescription={jobDescription?.description ?? ''} 
                      />
                      <div className="flex justify-center items-center py-4">
                        <Loader className="h-8 w-8 text-indigo-500 animate-spin mr-3" />
                        <span className="text-indigo-700 font-medium">Analyzing more candidates...</span>
                      </div>
                    </>
                  ) : (
            <AnalysisLoading />
                  )}
                </div>
              </div>
          ) : analyzedCandidates.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-blue-500 p-4 flex justify-between items-center text-white">
                  <h2 className="font-bold text-lg flex items-center">
                    <BarChart className="h-5 w-5 mr-2" />
                    Analysis Results
                  </h2>
                  {(userUsage.currentPlan === SubscriptionPlan.STANDARD || userUsage.currentPlan === SubscriptionPlan.PREMIUM) && (
                    <button
                      onClick={handleExportClick}
                      disabled={isExporting}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center ${isExporting ? 'bg-indigo-300 text-indigo-100 cursor-not-allowed' : 'bg-indigo-400 hover:bg-indigo-300 text-white'}`}
                      title="Export results to CSV (Standard/Premium only)"
                    >
                      {isExporting ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {isExporting ? 'Exporting...' : 'Export CSV'}
                    </button>
                  )}
                </div>
                <div className="p-1">
                  <AnalysisResults 
                    results={analyzedCandidates} 
                    isLoading={false} 
                    jobDescription={jobDescription?.description ?? ''} 
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Dashboard section */}
        {activeSection === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">
                  {t('dashboard.title', 'Dashboard')}
                </h1>
                {supabaseUser && (
                  <p className="text-gray-600 mt-1">
                    {t('dashboard.welcome', 'Welcome')}, {supabaseUser.user_metadata?.full_name || supabaseUser.email || 'User'}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (supabaseUser?.id) {
                      forceRefreshSubscription(supabaseUser.id)
                        .then(() => {
                          refreshUsageInfo();
                        });
                    }
                  }}
                  className="p-1 rounded-full hover:bg-gray-100"
                  title="Refresh subscription data"
                >
                  <RefreshCw className="h-5 w-5 text-gray-500 hover:text-indigo-600" />
                </button>
              </div>
            </div>
            
            {/* Usage Dashboard component showing subscription status */}
            <UsageDashboard className="mb-8 shadow-md" showDetailedView={true} />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Stats cards */}
              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Total Analyses</h3>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <BarChart className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-indigo-600">
                  {userUsage.totalUploads - userUsage.remainingUploads}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Analyses performed this billing cycle
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Remaining</h3>
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Search className="h-6 w-6 text-green-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-green-600">
                  {userUsage.remainingUploads}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Analyses remaining this billing cycle
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Current Plan</h3>
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Package className="h-6 w-6 text-purple-500" />
                  </div>
                </div>
                <p className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">
                  {userUsage.currentPlan.charAt(0).toUpperCase() + userUsage.currentPlan.slice(1)}
                </p>
                <Link 
                  to="/pricing" 
                  className="text-sm text-indigo-600 hover:text-indigo-800 mt-2 inline-block font-medium"
                >
                  Upgrade plan →
                </Link>
              </div>
            </div>
            
            {/* Admin Dashboard */}
            {userUsage.isAdmin && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Admin Controls</h2>
                <SubscriptionAdminDashboard isAdmin={userUsage.isAdmin} />
            </div>
          )}
            
            <div className="bg-white p-6 rounded-2xl shadow-md">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Activity</h2>
              <p className="text-gray-600">Your recent analysis history and activity will be displayed here.</p>
              <div className="mt-6 text-center p-10 border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-gray-500">No recent activity to display</p>
                <button
                  onClick={() => setActiveSection('analyzer')}
                  className="mt-4 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg font-medium hover:bg-indigo-200 transition-colors"
                >
                  Start a new analysis
                </button>
        </div>
            </div>
          </div>
        )}
      </main>

      {/* Usage limit modal */}
        <UsageLimitModal
          isOpen={showUsageModal}
          onClose={() => setShowUsageModal(false)}
          remainingUploads={userUsage.remainingUploads}
          isAdmin={userUsage.isAdmin}
        currentPlan={userUsage.currentPlan}
      />
    </div>
  );
}

function AuthPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const location = useLocation();
  const [isSignUp, setIsSignUp] = useState(false);
  
  // Sign up form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      const redirectTo = (location.state as any)?.from?.pathname || '/app';
      navigate(redirectTo, { replace: true });
    }
  }, [session, navigate, location.state]);

  // Handle sign up with extra fields
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      setError('All fields are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setMessage(null); // Reset any previous messages
      
      // Create the account with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`
          },
          emailRedirectTo: `${window.location.origin}/app` // Ensure redirect works properly
        }
      });
      
      if (error) {
        throw error;
      }
      
      // Display confirmation message regardless of email confirmation requirement
      setMessage('Your account has been created! Please check your email for the confirmation link.');
      
      // Create or update profile with names - we also have a trigger on auth.users for this
      if (data?.user?.id) {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            full_name: `${firstName.trim()} ${lastName.trim()}`,
            email: email.toLowerCase().trim(),
            updated_at: new Date().toISOString()
          });
          
        if (profileError) {
          console.error('Error updating profile:', profileError);
          // Don't throw error here, as the signup was still successful
        }
      }
      
      console.log('User signed up successfully with name data:', { firstName, lastName });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sign up');
      console.error('Sign up error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (session) {
    return <div className="min-h-screen flex items-center justify-center"><p>Redirecting...</p></div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
         <div className="text-center mb-6">
             <BrainCircuit className="h-10 w-10 text-indigo-600 mx-auto mb-3" />
             <h2 className="text-2xl font-bold text-gray-800">Welcome to IRecruitor</h2>
             <p className="text-gray-600">Sign in or create an account</p>
         </div>

        {/* Tab buttons for Sign In / Sign Up */}
        <div className="flex mb-6 border-b border-gray-200">
          <button
            className={`flex-1 py-2 text-center ${!isSignUp ? 'text-indigo-600 border-b-2 border-indigo-600 font-medium' : 'text-gray-500'}`}
            onClick={() => setIsSignUp(false)}
          >
            Sign In
          </button>
          <button
            className={`flex-1 py-2 text-center ${isSignUp ? 'text-indigo-600 border-b-2 border-indigo-600 font-medium' : 'text-gray-500'}`}
            onClick={() => setIsSignUp(true)}
          >
            Sign Up
          </button>
        </div>

        {/* Error and Success Message Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md">
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        {message && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-600 rounded-md">
            <p className="text-sm">{message}</p>
          </div>
        )}

        {isSignUp ? (
          // Custom Sign Up Form
          <div>
            <form className="space-y-4" onSubmit={handleSignUp}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${window.location.origin}/app` } })}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4"/>
                </svg>
                Google
              </button>
              <button
                type="button"
                onClick={() => supabase.auth.signInWithOAuth({ provider: 'apple', options: { redirectTo: `${window.location.origin}/app` } })}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.6 13.8c0-3 2.5-4.5 2.6-4.6-1.4-2.1-3.6-2.4-4.4-2.4-1.9-.2-3.6 1.1-4.6 1.1-.9 0-2.4-1.1-4-1-2 0-3.9 1.2-5 3-2.1 3.7-.5 9.1 1.5 12.1 1 1.5 2.2 3.1 3.8 3 1.5-.1 2.1-1 3.9-1s2.4.9 4 .9 2.7-1.5 3.7-2.9c1.2-1.7 1.6-3.3 1.7-3.4-.1-.1-3.2-1.3-3.2-4.8zm-3-8.9c.8-1 1.4-2.4 1.2-3.8-1.2.1-2.7.8-3.5 1.8-.8.9-1.5 2.3-1.3 3.7 1.4.1 2.8-.7 3.6-1.7z" fill="#000000"/>
                </svg>
                Apple
              </button>
            </div>
          </div>
        ) : (
          // Regular Sign In Form
          <Auth
            supabaseClient={supabase}
            view="sign_in"
            appearance={{ 
              theme: ThemeSupa,
              style: {
                button: { background: '#4F46E5', color: 'white' },
                anchor: { color: '#4F46E5' },
              }
            }}
            providers={['google', 'apple']}
            redirectTo={`${window.location.origin}/app`}
            localization={{
              variables: {
                sign_in: {
                  email_label: 'Email address',
                  password_label: 'Password',
                },
                sign_up: {
                  email_label: 'Email address',
                  password_label: 'Password',
                  button_label: 'Sign up',
                  link_text: 'Don\'t have an account? Sign up',
                  confirmation_text: 'Check your email for the confirmation link',
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { session, loading } = useAuth();
    const location = useLocation();

    if (loading) {
       return <div className="min-h-screen flex items-center justify-center"><p>Checking authentication...</p></div>;
    }

    if (!session) {
       return <Navigate to="/auth" state={{ from: location }} replace />;
    }

    return <>{children}</>;
}

export default function App() {
  return (
      <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/auth" element={<AuthPage />} /> 
            <Route 
               path="/app" 
               element={
                 <ProtectedRoute>
            <AppContent />
                 </ProtectedRoute>
               } 
            /> 
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </LanguageProvider>
  );
}